import { useState, FormEvent } from 'react';
import { useSearchParams } from '../navigation';
import { useInsforge } from '../provider/InsforgeProvider';
import { usePublicAuthConfig } from '../hooks/usePublicAuthConfig';
import { ResetPasswordForm } from './forms/ResetPasswordForm';
import { AuthContainer, AuthHeader } from './atoms';
import { createPasswordSchema } from '../lib/validation';

export interface ResetPasswordProps {
  /** Text customization */
  title?: string;
  subtitle?: string;
  newPasswordLabel?: string;
  newPasswordPlaceholder?: string;
  confirmPasswordLabel?: string;
  confirmPasswordPlaceholder?: string;
  submitButtonText?: string;
  loadingButtonText?: string;

  /** Callback when an error occurs */
  onError?: (error: Error) => void;
}

/**
 * Pre-built reset password page component with full business logic.
 *
 * Automatically reads the reset token from URL query parameter (?token=xxx).
 * Shows an error if token is missing.
 *
 * @component
 * @example
 * ```tsx
 * // Used at /reset-password?token=xxxxx
 * <ResetPassword />
 * ```
 */
export function ResetPassword({ onError, ...uiProps }: ResetPasswordProps) {
  const searchParams = useSearchParams();
  const token = searchParams.get('token');
  const { resetPassword } = useInsforge();
  const { authConfig } = usePublicAuthConfig();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Check if authConfig is loaded
    if (!authConfig) {
      setError('Configuration not loaded. Please refresh the page.');
      setLoading(false);
      return;
    }

    // Check if passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate token
    if (!token) {
      setError('Reset token is missing');
      setLoading(false);
      return;
    }

    // Validate password using dynamic Zod schema based on config
    const passwordZodSchema = createPasswordSchema({
      minLength: authConfig.passwordMinLength,
      requireUppercase: authConfig.requireUppercase,
      requireLowercase: authConfig.requireLowercase,
      requireNumber: authConfig.requireNumber,
      requireSpecialChar: authConfig.requireSpecialChar,
    });

    const passwordValidation = passwordZodSchema.safeParse(newPassword);
    if (!passwordValidation.success) {
      const firstError = passwordValidation.error.issues[0];
      setError(firstError.message);
      setLoading(false);
      return;
    }

    try {
      const result = await resetPassword(token, newPassword);

      if (result?.message) {
        setSuccess(true);
      } else {
        const errorMessage = 'Failed to reset password';
        setError(errorMessage);
        if (onError) {
          onError(new Error(errorMessage));
        }
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset password';
      setError(errorMessage);
      if (onError) {
        onError(new Error(errorMessage));
      }
    } finally {
      setLoading(false);
    }
  }

  // If email auth config not loaded yet
  if (!authConfig) {
    return null;
  }

  // If no token, show error
  if (!token) {
    return (
      <AuthContainer>
        <AuthHeader title="Invalid Reset Link" subtitle="" />
        <div
          style={{
            padding: '1.5rem',
            backgroundColor: '#FEE2E2',
            borderRadius: '0.5rem',
            border: '1px solid #DC2626',
          }}
        >
          <p
            style={{
              fontSize: '0.875rem',
              color: '#DC2626',
              margin: 0,
              fontFamily: 'var(--if-font-family)',
            }}
          >
            The password reset link is missing or invalid. Please request a new password reset link.
          </p>
        </div>
      </AuthContainer>
    );
  }

  // If success, show completion message
  if (success) {
    return (
      <AuthContainer>
        <div
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}
        >
          <div
            style={{
              width: '4rem',
              height: '4rem',
              borderRadius: '50%',
              backgroundColor: '#D1FAE5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg
              style={{ width: '2rem', height: '2rem', color: '#059669' }}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2
            style={{
              fontSize: '1.5rem',
              fontWeight: 600,
              color: '#000',
              margin: 0,
              textAlign: 'center',
              fontFamily: 'var(--if-font-family)',
            }}
          >
            Password Reset Successfully
          </h2>
          <p
            style={{
              fontSize: '0.875rem',
              color: '#828282',
              textAlign: 'center',
              margin: 0,
              fontFamily: 'var(--if-font-family)',
            }}
          >
            Your password has been reset successfully. You can now close this page and return to the
            login page in your original tab.
          </p>
        </div>
      </AuthContainer>
    );
  }

  // Show reset password form
  return (
    <ResetPasswordForm
      newPassword={newPassword}
      confirmPassword={confirmPassword}
      onNewPasswordChange={setNewPassword}
      onConfirmPasswordChange={setConfirmPassword}
      onSubmit={(e) => void handleSubmit(e)}
      error={error}
      loading={loading}
      success={false}
      authConfig={authConfig}
      {...uiProps}
    />
  );
}
